import { LINE_ITEM_COLORS } from "./chartColors";
import type { CostEstimate, LineItem } from "./types";

export type CostMixDonutSlice =
  | {
      kind: "sector";
      id: string;
      path: string;
      fill: string;
      row: LineItem;
    }
  | {
      kind: "ring";
      id: string;
      fill: string;
      row: LineItem;
    };

/** ViewBox units: 100×100, centered donut. */
export const COST_MIX_DONUT = {
  vb: 100,
  cx: 50,
  cy: 50,
  rOuter: 38,
  rInner: 22,
} as const;

export function annularSectorPath(
  cx: number,
  cy: number,
  rOuter: number,
  rInner: number,
  a0: number,
  a1: number,
): string {
  const span = a1 - a0;
  if (span <= 1e-7) return "";
  const large = span > Math.PI ? 1 : 0;
  const x0o = cx + rOuter * Math.cos(a0);
  const y0o = cy + rOuter * Math.sin(a0);
  const x1o = cx + rOuter * Math.cos(a1);
  const y1o = cy + rOuter * Math.sin(a1);
  const x1i = cx + rInner * Math.cos(a1);
  const y1i = cy + rInner * Math.sin(a1);
  const x0i = cx + rInner * Math.cos(a0);
  const y0i = cy + rInner * Math.sin(a0);
  return `M ${x0o} ${y0o} A ${rOuter} ${rOuter} 0 ${large} 1 ${x1o} ${y1o} L ${x1i} ${y1i} A ${rInner} ${rInner} 0 ${large} 0 ${x0i} ${y0i} Z`;
}

export function buildCostMixDonutSlices(estimate: CostEstimate): CostMixDonutSlice[] {
  const total = estimate.totalMonthlyUsd;
  const rows = estimate.lineItems.filter((r) => r.monthlyUsd > 0);
  if (total <= 0 || rows.length === 0) return [];

  const { cx, cy, rOuter, rInner } = COST_MIX_DONUT;
  const tau = 2 * Math.PI;

  if (rows.length === 1) {
    const row = rows[0];
    return [
      {
        kind: "ring",
        id: row.id,
        fill: LINE_ITEM_COLORS[row.id] ?? "#64748b",
        row,
      },
    ];
  }

  let acc = 0;
  const out: CostMixDonutSlice[] = [];
  for (const row of rows) {
    const a0 = (acc / total) * tau - Math.PI / 2;
    acc += row.monthlyUsd;
    const a1 = (acc / total) * tau - Math.PI / 2;
    out.push({
      kind: "sector",
      id: row.id,
      path: annularSectorPath(cx, cy, rOuter, rInner, a0, a1),
      fill: LINE_ITEM_COLORS[row.id] ?? "#64748b",
      row,
    });
  }
  return out;
}
